/**
 * Distribute a total across buckets proportional to the given percentages.
 * Works in cents to avoid floating-point drift, then converts back to euros.
 * Uses largest-remainder method so the amounts always sum exactly to `total`.
 */
export function distributeAmount(percentages: number[], total: number): number[] {
	const totalCents = Math.round(total * 100);
	const amounts = percentages.map((pct) => Math.floor((totalCents * pct) / 100));
	let remainder = totalCents - amounts.reduce((s, a) => s + a, 0);

	// Distribute remainder to entries with the largest fractional parts first
	const fractionals = percentages.map((pct, i) => ({
		index: i,
		frac: ((totalCents * pct) / 100) % 1,
	}));
	fractionals.sort((a, b) => b.frac - a.frac);

	for (const { index } of fractionals) {
		if (remainder <= 0) break;
		amounts[index]! += 1;
		remainder -= 1;
	}
	return amounts.map((a) => a / 100);
}

export const categoriesKeys = [
	'basicNeeds',
	'humanRights',
	'education',
	'emergency',
	'feeding',
	'environment',
	'habitat',
	'health',
] as const;

export const categoryDisplayOrder = [
	'health',
	'basicNeeds',
	'humanRights',
	'emergency',
	'feeding',
	'habitat',
	'environment',
	'education',
] as const;

export interface ChargeItem {
	key: string;
	percentage: number;
}

function sortByDisplayOrder(entries: [string, number][]): void {
	entries.sort(
		([a], [b]) =>
			categoryDisplayOrder.indexOf(a as (typeof categoryDisplayOrder)[number]) -
			categoryDisplayOrder.indexOf(b as (typeof categoryDisplayOrder)[number]),
	);
}

/**
 * Build charge items from allocations and a set of active category keys.
 *
 * - Active items (in `activeKeys`) receive their proportional allocation PLUS
 *   an even share of any inactive items' amounts.
 * - Inactive items keep their original percentage for display but their money
 *   goes to active items.
 * - If ALL items are inactive, each keeps its original percentage.
 */
function buildChargeItems(allocations: Record<string, number>, activeKeys: ReadonlySet<string>): ChargeItem[] {
	const entries = Object.entries(allocations).filter(([, pct]) => pct > 0);
	if (entries.length === 0) return [];

	sortByDisplayOrder(entries);

	let inactiveTotalPct = 0;
	let activeTotalPct = 0;
	let activeCount = 0;
	for (const [key, pct] of entries) {
		if (activeKeys.has(key)) {
			activeCount++;
			activeTotalPct += pct;
		} else {
			inactiveTotalPct += pct;
		}
	}

	if (activeCount > 0 && inactiveTotalPct > 0) {
		const redistributedPcts: number[] = [];
		for (const [key, pct] of entries) {
			if (!activeKeys.has(key)) {
				redistributedPcts.push(pct);
			} else {
				const proportionalExtra = activeTotalPct > 0 ? (pct / activeTotalPct) * inactiveTotalPct : 0;
				redistributedPcts.push(pct + proportionalExtra);
			}
		}
		return entries.map(([key], i) => ({
			key,
			percentage: redistributedPcts[i]!,
		}));
	}

	return entries.map(([key, pct]) => ({
		key,
		percentage: pct,
	}));
}

export interface CategoryResult {
	key: string;
	amount: number;
	percentage: number;
}

export interface CategoriesCalculation {
	/** All categories in order. Active ones have redistributed amounts, inactive ones have zero. */
	categories: CategoryResult[];
	/** The single fallback entry (summed amount from all inactive categories). Undefined if none. */
	fallback: { amount: number; percentage: number; fallbackKeys: string[] } | undefined;
}

/**
 * Calculate category amounts from allocations with redistribution.
 *
 * - Active categories (in `activeKeys`): get their proportional allocation + redistributed inactive amounts
 * - Inactive categories: amount is zero (their money goes to active ones)
 * - Fallback summary: the total amount from all inactive categories, ready to assign to a fallback campaign
 */
function calculateCategoryCampaigns(
	allocations: Record<string, number>,
	activeKeys: ReadonlySet<string>,
	totalAmount: number,
): CategoriesCalculation {
	const allPcts = Object.entries(allocations).filter(([, pct]) => pct > 0);
	if (allPcts.length === 0) return { categories: [], fallback: undefined };

	sortByDisplayOrder(allPcts);
	const originalAmounts = distributeAmount(
		allPcts.map(([, pct]) => pct),
		totalAmount,
	);
	const originalByKey = new Map(allPcts.map(([key], i) => [key, originalAmounts[i]!]));

	const items = buildChargeItems(allocations, activeKeys);
	const activeItems = items.filter((item) => activeKeys.has(item.key));
	const activeAmounts = distributeAmount(
		activeItems.map((item) => item.percentage),
		totalAmount,
	);
	const activeByKey = new Map(activeItems.map((item, i) => [item.key, activeAmounts[i]!]));

	const categories = items.map((item) => {
		const isActive = activeKeys.has(item.key);
		const amount = isActive ? (activeByKey.get(item.key) ?? 0) : 0;
		return {
			key: item.key,
			amount,
			percentage: totalAmount > 0 ? Math.round((amount / totalAmount) * 100) : 0,
		};
	});

	const fallbackKeys = items.filter((item) => !activeKeys.has(item.key)).map((item) => item.key);
	const fallbackAmount = fallbackKeys.reduce((sum, key) => sum + (originalByKey.get(key) ?? 0), 0);

	return {
		categories,
		fallback:
			fallbackAmount > 0
				? {
						amount: fallbackAmount,
						percentage: totalAmount > 0 ? Math.round((fallbackAmount / totalAmount) * 100) : 0,
						fallbackKeys,
					}
				: undefined,
	};
}

export interface RecurringCampaignToCharge<C = unknown> {
	category: string;
	campaign?: C;
	amount?: number;
	isFallback: boolean;
}

/**
 * Calculate recurring campaigns to charge from allocations and available campaigns.
 *
 * Each campaign has `categories: number[]` (category indices). The function
 * resolves them to category keys and matches against allocation keys.
 * Allocation categories matching a campaign → active (charged).
 * Allocation categories without a matching campaign → inactive (money redistributed).
 * If inactive categories exist, a fallback entry is created with the redirected amount.
 *
 * Campaigns are filtered by active status (not disabled, within date range).
 * When `donorCountry` / `donorCity` are provided, campaigns are ranked by
 * location proximity (city → country → most recent start date → id).
 */
export function calculateRecurringCampaignsToCharge<
	C extends {
		categories: number[];
		id?: string;
		disabled?: boolean;
		startDate?: string;
		endDate?: string;
		cityName?: string;
		countryCode?: string;
	},
>({
	campaigns,
	allocations,
	totalAmount,
	donorCountry,
	donorCity,
}: {
	campaigns: C[];
	allocations: Record<string, number>;
	totalAmount: number;
	donorCountry?: string;
	donorCity?: string;
}): RecurringCampaignToCharge<C>[] {
	const activeCampaigns = campaigns.filter(isCampaignActive);

	const campaignCategoryKeys = new Set<string>();
	for (const campaign of activeCampaigns) {
		for (const idx of campaign.categories) {
			const key = categoriesKeys[idx];
			if (key) campaignCategoryKeys.add(key);
		}
	}
	const activeKeys = new Set(
		Object.entries(allocations)
			.filter(([key, pct]) => pct > 0 && campaignCategoryKeys.has(key))
			.map(([key]) => key),
	);

	// Select best campaign per allocation category
	const campaignByKey = new Map<string, C>();
	if (activeCampaigns.length > 0) {
		for (const [key, pct] of Object.entries(allocations)) {
			if (pct <= 0) continue;
			const matches = activeCampaigns.filter((c) => c.categories.some((idx) => categoriesKeys[idx] === key));
			const pool = matches.length > 0 ? matches : activeCampaigns;
			campaignByKey.set(key, selectBestCampaign(pool, donorCountry, donorCity));
		}
	}

	const { categories, fallback } = calculateCategoryCampaigns(allocations, activeKeys, totalAmount);

	const recurringCampaigns: RecurringCampaignToCharge<C>[] = categories.map((cat) => ({
		category: cat.key,
		campaign: activeKeys.has(cat.key) && cat.amount > 0 ? campaignByKey.get(cat.key) : undefined,
		amount: activeKeys.has(cat.key) && cat.amount > 0 ? cat.amount : undefined,
		isFallback: false,
	}));

	// Only surface a fallback entry when NO preferred category has an active campaign.
	// When at least one preferred category is active, its amount already absorbs the redistributed
	// share via `buildChargeItems`, so emitting a separate fallback would double-count.
	if (fallback && activeKeys.size === 0) {
		recurringCampaigns.push({
			category: fallback.fallbackKeys[0]!,
			campaign: campaignByKey.get(fallback.fallbackKeys[0]!),
			amount: fallback.amount,
			isFallback: true,
		});
	}

	recurringCampaigns.sort((a, b) => (b.amount ?? -Infinity) - (a.amount ?? -Infinity));

	return recurringCampaigns;
}

/** Sorts recurring campaigns so that categories without an active campaign come last. */
export function sortRecurringCampaignsActiveFirst<C>(
	recurringCampaigns: RecurringCampaignToCharge<C>[],
): RecurringCampaignToCharge<C>[] {
	return [...recurringCampaigns].toSorted((a, b) => {
		const aInactive = a.amount === undefined ? 1 : 0;
		const bInactive = b.amount === undefined ? 1 : 0;
		return aInactive - bInactive;
	});
}

function isCampaignActive(campaign: { disabled?: boolean; startDate?: string; endDate?: string }): boolean {
	if (campaign.disabled) return false;
	const now = new Date();
	if (campaign.startDate && new Date(campaign.startDate) > now) return false;
	if (campaign.endDate && new Date(campaign.endDate) <= now) return false;
	return true;
}

function selectBestCampaign<C extends { id?: string; startDate?: string; cityName?: string; countryCode?: string }>(
	campaigns: C[],
	donorCountry?: string,
	donorCity?: string,
): C {
	const sorted = campaigns.toSorted((a, b) => {
		const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
		const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
		return dateB - dateA || (a.id ?? '').localeCompare(b.id ?? '');
	});

	if (donorCity) {
		const cityMatch = sorted.find((c) => c.cityName?.toLowerCase() === donorCity.toLowerCase());
		if (cityMatch) return cityMatch;
	}

	if (donorCountry) {
		const countryMatch = sorted.find((c) => c.countryCode === donorCountry);
		if (countryMatch) return countryMatch;
	}

	return sorted[0]!;
}

// prettier-ignore
const HTML_ENTITIES: Record<string, string> = {
	amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: '\u00A0',
	Aacute: 'Á', aacute: 'á', Eacute: 'É', eacute: 'é',
	Iacute: 'Í', iacute: 'í', Oacute: 'Ó', oacute: 'ó',
	Uacute: 'Ú', uacute: 'ú', Ntilde: 'Ñ', ntilde: 'ñ',
	Uuml: 'Ü', uuml: 'ü', iquest: '¿', iexcl: '¡',
	Agrave: 'À', agrave: 'à', Egrave: 'È', egrave: 'è',
	Igrave: 'Ì', igrave: 'ì', Ograve: 'Ò', ograve: 'ò',
	Ugrave: 'Ù', ugrave: 'ù', Acirc: 'Â', acirc: 'â',
	Ecirc: 'Ê', ecirc: 'ê', Icirc: 'Î', icirc: 'î',
	Ocirc: 'Ô', ocirc: 'ô', Ucirc: 'Û', ucirc: 'û',
	Atilde: 'Ã', atilde: 'ã', Otilde: 'Õ', otilde: 'õ',
	Ccedil: 'Ç', ccedil: 'ç', euro: '€', pound: '£',
	copy: '©', reg: '®', trade: '™', hellip: '…',
	mdash: '—', ndash: '–', laquo: '«', raquo: '»',
};
const HTML_ENTITY_RE = /&(?:#(\d+)|#x([\da-fA-F]+)|(\w+));/g;
const HTML_TAG_RE = /<[^>]*>/g;

export function stripHtml(str: string): string {
	// Decode HTML entities (e.g. &Oacute; → Ó), then strip tags
	const decoded = str.replace(HTML_ENTITY_RE, (match, dec, hex, named) => {
		if (dec) return String.fromCodePoint(Number(dec));
		if (hex) return String.fromCodePoint(Number.parseInt(hex, 16));
		return HTML_ENTITIES[named] ?? match;
	});
	return decoded.replace(HTML_TAG_RE, '');
}

export function truncateString(str: string | undefined, targetSize: number): string | undefined {
	if (!str || str.length <= targetSize) return str;
	const stripped = stripHtml(str);
	if (stripped.length <= targetSize) return stripped;
	// Find last space within targetSize to avoid cutting words
	const lastSpace = stripped.lastIndexOf(' ', targetSize);
	const cutoff = lastSpace > 0 ? lastSpace : targetSize;
	return `${stripped.substring(0, cutoff).trimEnd()}…`;
}

export function isEndDatePast(endDate?: string): boolean {
	if (!endDate) return false;
	const parsed = new Date(endDate).getTime();
	if (Number.isNaN(parsed)) return false;
	return parsed < Date.now();
}

export function getEffectiveStatus(project: { status: string; endDate?: string }): string {
	if (project.status === 'funded') return 'funded';
	if (project.status !== 'paused' && isEndDatePast(project.endDate)) return 'funded';
	return project.status;
}

declare function btoa(data: string): string;

const imagePathCache = new Map<string, string>();
const IMAGE_PATH_CACHE_MAX = 50_000;

export function generateImagePath(
	src: string,
	opts?: { height?: number; width?: number; format?: 'webp' | 'avif'; quality?: number; resizeType?: 'fill' | 'fit' },
): string {
	const h = opts?.height ?? 0;
	const w = opts?.width ?? 0;
	const format = opts?.format ?? 'webp';
	const quality = opts?.quality ?? 80;
	const rt = opts?.resizeType ?? 'fill';
	const cacheKey = src + h + ',' + w + ',' + format + ',' + quality + ',' + rt;

	const cached = imagePathCache.get(cacheKey);
	if (cached) return cached;

	const result =
		'/media/' +
		(h && w ? `rt:${rt}/` : '') +
		(h ? `h:${Number.isInteger(h) ? h : Math.floor(h)}/` : '') +
		(w ? `w:${Number.isInteger(w) ? w : Math.floor(w)}/` : '') +
		`q:${quality}/` +
		btoa(src) +
		(src.endsWith('.svg') ? '' : `.${format}`);

	if (imagePathCache.size >= IMAGE_PATH_CACHE_MAX) imagePathCache.clear();
	imagePathCache.set(cacheKey, result);

	return result;
}
